import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQueries } from '@tanstack/react-query';
import type { UseQueryResult } from '@tanstack/react-query';

import { useApiErrorMessage } from '@/shared/i18n/useApiErrorMessage';
import { useStableHandler } from '@/shared/lib/useStableHandler';
import { Button, Skeleton } from '@/shared/ui';

import * as elementsApi from '../api/elementsApi';
import { mergeHeatSnapshot } from '../model/heatSnapshot';
import { NEAR_SUMS_DEPTH, readSumsScope, sumsDepth } from '../model/sums';
import { buildRows, mergeViews, reuseRows, sectionKey } from '../model/tree';
import type { ElementRow, TreeRow } from '../model/tree';
import type { ConstellationView, ElementWorkCounts, RelationDirection } from '../model/types';
import {
  elementKeys,
  useConstellation,
  useElementOwners,
  useWorkRollup,
} from '../queries/useElement';
import styles from './Constellation.module.css';
import { ConstellationTree } from './ConstellationTree';
import { ElementInspector } from './ElementInspector';

/**
 * Ekran drzewka (`/constellation/{id}`, decyzja 23: ścieżki po angielsku).
 *
 * Stan widoku mieszka w URL-u (spec 4A: cała platforma jest sterowana adresem),
 * więc link odtwarza **ten sam ekran**, który widzi nadawca. Rozwinięcia zostają
 * w pamięci komponentu — są drogą kliknięć, a nie stanem do udostępniania.
 *
 * ⭐ Dwa gesty, dwa rodzaje wpisu w historii (decyzja 158):
 * - **zaznaczenie** (`?el=`) idzie przez `replace` — zaznaczanie to porównywanie sąsiadów,
 *   nie nawigacja; gdyby każde zajrzenie dokładało wpis, „wstecz" przestałby wracać tam,
 *   skąd człowiek przyszedł (piętnaście kliknięć w sąsiadów = piętnaście „wstecz");
 * - **wejście** (`/constellation/{id}`) idzie przez `push` — zmiana korzenia jest krokiem
 *   w śladzie (decyzja 52: pasek nad drzewkiem = droga kliknięć), a „wstecz" wraca do
 *   poprzedniego korzenia razem z jego zaznaczeniem, bo `?el=` siedzi we wpisie historii.
 *
 * ⚠ Wydajność (2026-08-26): wszystko, co dostaje zapamiętany wiersz drzewka, ma stałą
 * tożsamość — handlery przez `useStableHandler`, wiersze przez `reuseRows`, a `useQueries`
 * z `combine`, bo bez niego oddaje NOWĄ tablicę co render i każdy klawisz przeliczał scalanie
 * 600 węzłów z 1400 adnotacjami od zera.
 */

const LENSES: readonly RelationDirection[] = ['has', 'depends-on', 'used-by'];
const CAPS = [5, 7, 10, null] as const;

/**
 * Wspólny kształt trzech zapytań wsadowych drzewka — tyle, ile potrzeba, żeby powiedzieć
 * „odpowiedź jest niepełna". ⚠ Każde niesie inną mapę, więc typ jest celowo **węższy** od
 * nich wszystkich: dzięki temu strażnik nie ma dostępu do treści i nie da się go po cichu
 * rozbudować o zaglądanie do wartości.
 */
interface BatchQuery {
  isError: boolean;
  isPlaceholderData: boolean;
  data: { size: number } | undefined;
}

export function ConstellationScreen() {
  const { id } = useParams<{ id: string }>();
  const elementId = Number(id);

  if (!Number.isInteger(elementId) || elementId <= 0) {
    return <InvalidId />;
  }

  // Klucz wymusza świeży stan przy przejściu na inny element — bez tego
  // rozwinięcia poprzedniego korzenia zostałyby w pamięci i mieszały widok.
  return <ConstellationView key={elementId} elementId={elementId} />;
}

function InvalidId() {
  const { t } = useTranslation();

  return (
    <div className={styles['failure']} role="alert">
      <p>{t('graph.element.invalidId')}</p>
    </div>
  );
}

/**
 * Zebranie doładowanych podwidoków w jedną listę — funkcja na poziomie modułu, bo TanStack
 * zapamiętuje wynik `combine` tylko przy stałej tożsamości funkcji. Zdefiniowana w komponencie
 * dawałaby nową tablicę co render, a od niej zależy scalanie całego drzewka.
 */
function combineBranches(results: UseQueryResult<ConstellationView>[]): ConstellationView[] {
  return results.flatMap((result) => (result.data ? [result.data] : []));
}

function ConstellationView({ elementId }: { elementId: number }) {
  const { t } = useTranslation();
  const describeError = useApiErrorMessage();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();

  const cap = readCap(params.get('show'));
  const hidden = readLenses(params.get('rel'));

  // Zasięg agregatu `Σ` (decyzja 171). Domyślnie „cała gałąź" — czyli to, co `Σ` znaczyła
  // od decyzji 75; wąski zasięg jest INNYM pytaniem, nie tańszą wersją tego samego.
  const sumsScope = readSumsScope(params.get('sums'));

  /*
   * Zaznaczony element jest częścią widoku, więc mieszka w adresie (spec 4A) — link
   * niesie zaznaczenie, a odświeżenie je odtwarza. `panel=commitments` zostało z czasów
   * osobnego panelu zobowiązań i znaczy dziś „otwórz na sekcji Zobowiązania": produkcja ma
   * klientów, a linki w czatach żyją dłużej niż układ ekranu.
   */
  const selectedId = readSelectedElement(params);
  const wantsCommitments = params.get('panel') === 'commitments';

  /*
   * Który z wierszy zaznaczonego elementu jest tym KLIKNIĘTYM — stan komponentu, nie adres:
   * klucz wiersza zależy od rozwinięć, których adres nie zna. Po wejściu z linku pełny kolor
   * dostaje pierwszy wiersz elementu (patrz `primaryRowKey` niżej).
   */
  const [clickedKey, setClickedKey] = useState<string | null>(null);
  // Licznik żądań „przewiń do zobowiązań" — bez niego drugie kliknięcie w liczby tego samego
  // wiersza nic by nie przewinęło, bo adres by się nie zmienił.
  const [commitmentsNonce, setCommitmentsNonce] = useState(0);

  const [expanded, setExpanded] = useState<ReadonlySet<number>>(new Set());
  const [unrestricted, setUnrestricted] = useState<ReadonlySet<string>>(new Set());

  /*
   * ⭐⭐ Bez parametru głębokości (decyzja 174). Chip „Głębokość" zniknął, bo sterował ZAPASEM
   *    W KIESZENI, a nazywał to widokiem: drzewko rozwija wyłącznie korzeń, więc pobrane
   *    poziomy nigdy nie docierały do oczu — przełączenie 3→2→1 nie zmieniało ani jednego
   *    wiersza. Serwer ma dziś domyślną głębokość 1, czyli dokładnie tyle, ile widać.
   * ⛔ Parametru NIE podajemy też przy gałęziach: skoro wszędzie byłby ten sam, to jego brak
   *    jest uczciwszy — a dwie wartości dla tej samej treści dałyby DWA klucze zapytania
   *    i pobranie tego samego węzła drugi raz przy wejściu w niego.
   */
  const root = useConstellation(elementId);

  // ── Doładowania na żądanie ────────────────────────────────────────────────
  // Rozwinięcie poza to, co przyszło na starcie (decyzja 70: zależności są
  // adnotacją bez rozwijania). Pytamy TYLKO o węzły, o których naprawdę nic nie
  // wiemy — inaczej rozwinięcie czegoś, co już mamy, strzelałoby bez potrzeby.
  const loaded = useMemo(() => mergeViews(root.data ? [root.data] : []), [root.data]);

  const toFetch = useMemo(
    () =>
      [...expanded].filter((id) => {
        const node = loaded.nodes.get(id);
        if (!node) {
          return true; // wiersz zależności — nie znamy nawet jego liczników
        }

        if (node.childCount > 0 && !loaded.children.has(id)) {
          return true;
        }

        return node.dependsOnCount + node.usedByCount > 0 && !loaded.edges.has(id);
      }),
    [expanded, loaded],
  );

  const branches = useQueries({
    queries: toFetch.map((id) => ({
      // ⭐ Ten sam klucz co przy wejściu w węzeł (decyzja 174) — bo to ta sama treść.
      //   Dopóki gałąź miała własny wariant klucza, wejście w rozwinięty wcześniej węzeł
      //   pobierało go po raz drugi.
      queryKey: elementKeys.constellation(id),
      queryFn: ({ signal }: { signal: AbortSignal }) => elementsApi.fetchConstellation(id, signal),
    })),
    combine: combineBranches,
  });

  const constellation = useMemo(
    () => mergeViews(root.data ? [root.data, ...branches] : branches),
    [root.data, branches],
  );

  /*
   * ── Dwa pozostałe zapytania, RÓWNOLEGLE (decyzje 72, 73) ──────────────────
   *
   * ⛔⛔ Lista to węzły kręgosłupa **PLUS elementy z krawędzi**, i to nie jest drobiazg.
   *    `mergeViews` wkłada do `nodes` wyłącznie `spine` (przechód po `has`), a zależności
   *    idą do osobnej mapy `edges`. Do 2026-09-05 pytaliśmy o same `nodes`, więc element
   *    widoczny WYŁĄCZNIE jako wiersz `depends-on`/`used-by` **nie był pytany o nic** —
   *    rysowaliśmy dla niego wiersz i nigdy nie pytaliśmy o jego właściciela ani liczby.
   *    ⚠ Objaw wyszedł dopiero po decyzji 167: wcześniej taki wiersz malował pusty slot
   *    właściciela i `—` przy liczbach, czyli **kłamał cicho** („nikt nie odpowiada, zero
   *    roboty"), a dziś mówi uczciwe `?`. Usterka jest starsza niż jej objaw.
   *    Zmierzone na demo 2026-09-05: 5 z 16 wierszy z `?`, dokładnie te z kluczem `e*`.
   */
  /*
   * ⭐⭐ Liczby pracy NIE są już osobnym zapytaniem (decyzja 173) — przychodzą razem z kształtem,
   *    a zbiór elementów do policzenia składa SERWER. To zdejmuje z frontu obowiązek, w którym
   *    raz już się pomylił: `visibleIds` była unią dwóch map i zapominała o celach adnotacji,
   *    więc wiersz zależności rysował się nigdy o nic nie pytany (decyzja 168).
   * ⚠ Brak wpisu dalej znaczy „nie policzono" (`?`, decyzja 167), nie „zero".
   */
  const work = constellation.work;

  /**
   * ⛔⛔ Czwarty stan, którego te trzy zapytania nie miały WCALE (CLAUDE.md: każdy ekran ma
   * ładowanie · pustkę · **błąd z akcją** · sukces). Do 2026-09-04 ich pad przechodził bez
   * słowa, a wiersz rysował brak odpowiedzi jako `—`, czyli jak „zero roboty".
   *
   * ⚠ Dwie różne niepełności, obie kłamały tak samo: **pad** (`isError`) i **przycięcie**
   * po stronie serwera. Drugie poznajemy po tym, że wróciło mniej wierszy, niż wysłaliśmy
   * identyfikatorów — serwer oddaje wiersz na KAŻDY policzony identyfikator, także na taki,
   * którego nie ma w bazie, więc ta różnica nie ma innego źródła.
   *
   * ⚠ `isPlaceholderData` jest w warunku nieprzypadkowo: każde rozwinięcie gałęzi zmienia
   * klucz zapytania, a `keepPreviousData` trzyma wtedy odpowiedź na POPRZEDNI, krótszy zbiór.
   * Bez tego warunku pasek mrugałby przy każdym kliknięciu w trójkąt.
   */
  const incomplete = (query: BatchQuery, expected: number) =>
    query.isError ||
    (!query.isPlaceholderData && query.data !== undefined && query.data.size < expected);

  // Poprzednie wiersze po kluczu — żeby po przeliczeniu oddać TE SAME obiekty tam, gdzie nic
  // się nie zmieniło; zapamiętany wiersz porównuje `row` po tożsamości.
  const previousRows = useRef<ReadonlyMap<string, TreeRow>>(new Map());

  /*
   * ⭐⭐ CIEPŁO MA DWIE ROLE, I TYLKO JEDNA Z NICH MOŻE BYĆ ŻYWA (decyzja 172).
   *
   * - **Do wyświetlenia** liczba ma być świeża — to `work`, jedzie do wiersza niżej.
   * - **Do WYBORU widocznej siódemki** (decyzja 45) musi być ZAMROŻONA, bo inaczej domknięcie
   *   jednego zadania przestawia, kto przechodzi przez próg, i wiersze skaczą człowiekowi pod
   *   ręką. ⚠ To działo się już przed tą zmianą: `useCommitments` unieważnia `work-counts`
   *   po każdej mutacji, a stamtąd szedł wprost wybór.
   *
   * Słowo usera 2026-09-06: *„po zamknięciu zadania nie odświeżamy listy co do elementów
   * i nie przenosimy elementu niżej, bo ma teraz mniej ciepła […] dopiero kolejne odświeżenie
   * drzewka zaciągnie i posortuje listę"*.
   *
   * ⛔ Wariantu dosłownego („odejmij lokalnie i nie pytaj serwera") NIE robimy: domknięcie
   *   zadania **budzi albo osierocia zobowiązania na cudzych elementach** (decyzje 21–22), więc
   *   liczby zmieniają się w miejscach, o których ta mutacja nic nie wie. Zamrożenie dotyczy
   *   WYBORU, nie prawdy.
   *
   * Migawka **dokleja** wpisy dla elementów, których jeszcze nie znała (rozwinięta gałąź musi
   * dostać swoje ciepło), a **zeruje się** dopiero przy zmianie widoku: inny korzeń, inna
   * soczewka, inny próg. ⚠ Odświeżenie danych samo w sobie jej NIE zeruje — powrót do karty
   * nie jest powodem, żeby wiersze się przestawiły.
   */
  const heatKey = `${elementId}|${cap ?? 'all'}|${[...hidden].sort().join(',')}`;
  const heatSnapshot = useRef<{ key: string; map: ReadonlyMap<number, ElementWorkCounts> }>({
    key: heatKey,
    map: new Map(),
  });

  const heat = useMemo(() => {
    const previous = heatSnapshot.current;
    const next = mergeHeatSnapshot({
      previous: previous.map,
      live: work,
      stale: previous.key !== heatKey,
    });

    heatSnapshot.current = { key: heatKey, map: next };

    return next;
  }, [work, heatKey]);

  const rows = useMemo(() => {
    const next = reuseRows(
      previousRows.current,
      buildRows(constellation, {
        expanded,
        siblingCap: cap,
        hiddenSections: hidden,
        unrestricted,
        // Ciepło WYBIERA widoczną siódemkę, nie sortuje jej (decyzja 45) — i robi to
        // z MIGAWKI, nie z żywej odpowiedzi (decyzja 172, komentarz wyżej).
        heat,
      }),
    );

    previousRows.current = new Map(next.map((row) => [row.key, row]));

    return next;
  }, [constellation, expanded, cap, hidden, unrestricted, heat]);

  /*
   * Czwarte zapytanie: agregat `Σ` po poddrzewie (decyzja 75). Osobno od `work`, bo to inny
   * koszt — tamto czyta wiersz na element, to **przechodzi graf**.
   *
   * ⭐⭐ Pytamy o wiersze NARYSOWANE, nie o wszystkie węzły — i to jedyne z czterech zapytań,
   *    które tak może. Sprawdzone w kodzie 2026-09-05: ciepło (decyzja 45) karmi `work`,
   *    więc zwężenie listy **nie dotyka wyboru widocznej siódemki**. Decyzja 167 odrzuciła
   *    wariant „pytaj tylko o widoczne" argumentem „zabiłby ciepło" — ⚠ ale ten argument
   *    dotyczy WYŁĄCZNIE `usage`; potraktowanie trzech zapytań jako jednej decyzji było za grube.
   *
   * ⭐ Znaczenie `Σ` zostaje nietknięte (dalej cała gałąź po `has`, to samo w każdej soczewce —
   *   decyzja 75b). Zmienia się tylko, o ILE kotwic pytamy, a kotwica bez narysowanego wiersza
   *   i tak nikomu niczego nie mówi. Zmierzone: przy węźle-hubie `Platform` zbiór kotwic spada
   *   z 3552 (86% sufitu rekurencji) do rzędu dziesiątek.
   */
  const renderedIds = useMemo(
    () => [...new Set(rows.filter((row) => row.kind === 'element').map((row) => row.elementId))],
    [rows],
  );

  const rollup = useWorkRollup(renderedIds, sumsDepth(sumsScope));

  /*
   * ⭐⭐ Właściciele też pytają WYŁĄCZNIE o narysowane wiersze — i to jest zmiana o koszt,
   *    nie o prawdę. Zmierzone 2026-09-05 w przeglądarce: jedno rozwinięcie dociągające węzły
   *    kosztowało **690 KB**, z czego `owners` **431 KB** — pobrane od nowa dla wszystkiego,
   *    żeby dołożyć trzydzieści wierszy. Klucz zapytania niesie całą listę identyfikatorów,
   *    więc dopisanie jednego tworzy INNE pytanie, a nie dopytanie.
   *
   * ⚠ Bezpieczne, bo `owners` nie karmi NICZEGO poza wierszem — sprawdzone: nie ma go
   *   w `tree.ts`, więc ani ciepło (decyzja 45), ani próg zwijania (decyzja 42) go nie widzą.
   *   ⛔ Dla `usage` tak zrobić NIE WOLNO: ciepło wybiera widoczną siódemkę spośród
   *   WSZYSTKICH pozycji sekcji, więc zwężenie karmiłoby wybór własnym wynikiem.
   */
  const owners = useElementOwners(renderedIds);

  /*
   * ⚠ Liczby pracy mierzą się inaczej niż pozostałe dwa zapytania (decyzja 173): nie mają
   *   własnej odpowiedzi, którą dałoby się policzyć, więc niepełność poznajemy po tym, że
   *   NARYSOWANY wiersz nie ma wpisu. Serwer oddaje wiersz dla każdego elementu odpowiedzi,
   *   więc luka ma dokładnie jedną przyczynę: port nie odpowiedział w budżecie.
   * ⛔ Nie mierzymy tego przy `root.isFetching` — w trakcie doładowania gałęzi nowe wiersze
   *   przez chwilę nie mają liczb i pasek mrugałby przy każdym trójkącie.
   */
  const workIncomplete =
    !root.isFetching && branches.length === toFetch.length && renderedIds.some((id) => !work.has(id));

  const numbersIncomplete =
    workIncomplete ||
    // ⚠ Te dwa mierzą się WŁASNĄ listą — porównanie z liczbą wczytanych węzłów zapalałoby pasek
    // zawsze, bo narysowanych wierszy jest z definicji mniej.
    incomplete(owners, renderedIds.length) ||
    incomplete(rollup, renderedIds.length);

  const retryNumbers = useStableHandler(() => {
    void Promise.all([root.refetch(), owners.refetch(), rollup.refetch()]);
  });

  // Wszystkie wiersze zaznaczonego BYTU — bywa ich więcej niż jeden (decyzja 157).
  const selectedRows = useMemo(
    () =>
      selectedId === null
        ? []
        : rows.filter(
            (row): row is ElementRow => row.kind === 'element' && row.elementId === selectedId,
          ),
    [rows, selectedId],
  );

  // Kliknięty wiersz, jeśli dalej jest na ekranie; inaczej pierwszy (wejście z adresu,
  // odświeżenie, zwinięcie gałęzi z klikniętym wierszem).
  const primaryRowKey = selectedRows.some((row) => row.key === clickedKey)
    ? clickedKey
    : (selectedRows[0]?.key ?? null);

  const toggle = useStableHandler((id: number) => {
    setExpanded((previous) => {
      const next = new Set(previous);
      if (!next.delete(id)) {
        next.add(id);
      }
      return next;
    });
  });

  const showAll = useStableHandler((anchorId: number, section: RelationDirection) => {
    setUnrestricted((previous) => new Set(previous).add(sectionKey(anchorId, section)));
  });

  function setParam(key: string, value: string | null) {
    const next = new URLSearchParams(params);
    if (value === null) {
      next.delete(key);
    } else {
      next.set(key, value);
    }
    setParams(next, { replace: true });
  }

  /** Bieżące ustawienia widoku z podmienionym zaznaczeniem — jeden kształt dla obu gestów. */
  function withSelection(id: number | null, atCommitments: boolean): URLSearchParams {
    const next = new URLSearchParams(params);
    next.delete('panel');
    next.delete('el');

    if (id !== null) {
      next.set('el', String(id));
      if (atCommitments) {
        next.set('panel', 'commitments');
      }
    }

    return next;
  }

  const select = useStableHandler((row: ElementRow) => {
    setClickedKey(row.key);
    setParams(withSelection(row.elementId, false), { replace: true });
  });

  const openCommitments = useStableHandler((row: ElementRow) => {
    setClickedKey(row.key);
    setCommitmentsNonce((nonce) => nonce + 1);
    setParams(withSelection(row.elementId, true), { replace: true });
  });

  const clearSelection = useStableHandler(() => {
    setClickedKey(null);
    setParams(withSelection(null, false), { replace: true });
  });

  /**
   * Adres wejścia: nowy korzeń zostaje zaznaczony, a głębokość, próg i soczewka JADĄ
   * z człowiekiem — do 2026-08-26 link nazwy je gubił (znalezisko z przeglądu 2026-08-25).
   */
  function enterPath(id: number): string {
    return `/constellation/${id}?${withSelection(id, false).toString()}`;
  }

  const enter = useStableHandler((row: ElementRow) => {
    // `push`, nie `replace` — patrz nagłówek pliku.
    void navigate(enterPath(row.elementId));
  });

  if (root.isPending) {
    return (
      <div className={styles['loading']} aria-busy="true">
        <Skeleton width="24rem" height="1.5rem" announce />
        <Skeleton height="1.5rem" />
        <Skeleton height="1.5rem" />
        <Skeleton height="1.5rem" />
      </div>
    );
  }

  if (root.isError) {
    return (
      <div className={styles['failure']} role="alert">
        <p>{describeError(root.error)}</p>
        <Button onClick={() => void root.refetch()}>{t('common.action.retry')}</Button>
      </div>
    );
  }

  // Tytuł i typ z drzewka, jeśli je zna — panel maluje nagłówek od razu, zanim przyjdzie
  // detal. Adnotacje zależności nie są w `nodes` (tylko w `edges`), stąd zapasowe źródło.
  const selectedNode = selectedId === null ? undefined : constellation.nodes.get(selectedId);
  const selectedTitle = selectedNode?.title ?? selectedRows[0]?.title;
  const selectedType = selectedNode?.type ?? selectedRows[0]?.type;

  return (
    <div className={styles['screen']}>
      <div className={styles['toolbar']}>
        <div className={styles['group']}>
          <span className={styles['groupLabel']}>{t('graph.constellation.lens')}</span>
          <button
            type="button"
            className={`${styles['chip']} ${hidden.size === 0 ? styles['chipOn'] : ''}`}
            onClick={() => setParam('rel', null)}
          >
            {t('graph.constellation.lensAll')}
          </button>
          {LENSES.map((lens) => (
            <button
              key={lens}
              type="button"
              className={`${styles['chip']} ${
                hidden.size > 0 && !hidden.has(lens) ? styles['chipOn'] : ''
              }`}
              // Soczewka to FILTR (decyzja 51) — chowa pozostałe sekcje,
              // nie zmienia zasady ani kolejności.
              onClick={() => setParam('rel', lens)}
            >
              {t(`graph.relation.${lens}`)}
            </button>
          ))}
        </div>

        <div className={styles['group']}>
          <span className={styles['groupLabel']}>{t('graph.constellation.showBy')}</span>
          {CAPS.map((option) => (
            <button
              key={String(option)}
              type="button"
              className={`${styles['chip']} ${cap === option ? styles['chipOn'] : ''}`}
              onClick={() => setParam('show', option === null ? 'all' : String(option))}
            >
              {option === null ? t('graph.constellation.showAllChip') : option}
            </button>
          ))}
        </div>

        {/*
          ZASIĘG `Σ` (decyzja 171, werdykt usera 2026-09-06) — w miejscu wcześniejszego
          przełącznika „pełne sumy", który zniknął razem z pustym slotem na liściu.

          ⭐⭐ To jest wybór PYTANIA, nie dokładności — i dlatego stoi nad drzewkiem, a nie
            w wierszu. Dwie potrzeby są prawdziwe naraz: „czy warto wejść w tę zwiniętą
            gałąź" (cała gałąź, decyzja 75) i „ile roboty jest blisko mnie". Trzecia grupa
            cyfr w wierszu odpadła — decyzja 166 właśnie zdjęła drugą, bo trzy skale obok
            siebie czytają się jak jedna.

          ⚠ Zawężenie NIE JEST przybliżeniem: zmierzone 2026-09-05, suma do jednego poziomu
            obejmuje 8,9 % roboty w gałęzi (robota wisi na liściach). Dlatego domyślny zasięg
            zostaje „cała gałąź" — przestawienie domyślnego zmieniłoby znaczenie każdej liczby
            na ekranie bez słowa.

          ⚠ Stan idzie do ADRESU (spec 4A) tak samo jak `show` — „policzyłem to i chcę Ci
            pokazać" musi dać się wkleić. ⭐ Stare linki `?sums=full` trafiają w domyślny
            zasięg i pokazują dokładnie to, o co prosiły: pełne sumy są dziś zawsze.
        */}
        <div className={styles['group']}>
          <span className={styles['groupLabel']}>{t('graph.constellation.sums')}</span>
          {(['branch', 'near'] as const).map((scope) => (
            <button
              key={scope}
              type="button"
              className={`${styles['chip']} ${sumsScope === scope ? styles['chipOn'] : ''}`}
              aria-pressed={sumsScope === scope}
              onClick={() => setParam('sums', scope === 'branch' ? null : scope)}
            >
              {/* ⛔ Klucze WYPISANE, nie składane w locie: skan pokrycia i18n
                  (`i18n.test.ts`) czyta literały `t('…')`, a klucz sklejony ze zmiennej
                  jest jego ślepą plamą — brak tłumaczenia wyszedłby dopiero na ekranie. */}
              {scope === 'branch'
                ? t('graph.constellation.sumsBranch')
                : t('graph.constellation.sumsNear', { levels: NEAR_SUMS_DEPTH })}
            </button>
          ))}
        </div>
      </div>

      {/* Obcięcie MUSI być widoczne — niepełne drzewo wzięte za pełne prowadzi
          do wniosku z brakujących danych. `output` zamiast `role="status"`: ma tę
          rolę natywnie, więc znacznik nie rozjedzie się z atrybutem przy edycji. */}
      {constellation.truncated ? (
        <output className={styles['truncated']}>{t('graph.constellation.truncated')}</output>
      ) : null}

      {/* ⚠ Drzewko ZOSTAJE na ekranie — struktura jest prawdziwa, nieprawdziwe były tylko
          liczby. Dlatego pasek nad listą, a nie ekran błędu zamiast listy jak przy padzie
          samego drzewka.
          ⚠ `output`, nie `div role="alert"` — z dwóch powodów naraz: to nie jest przerwanie
          pracy (więc obszar uprzejmy, nie alarm), a znacznik ma tę rolę NATYWNIE, więc nie
          rozjedzie się z atrybutem przy edycji. Ten sam wybór co przy pasku obcięcia wyżej. */}
      {numbersIncomplete ? (
        <output className={styles['numbersFailure']}>
          <span>{t('graph.constellation.numbersFailed')}</span>
          <Button onClick={retryNumbers}>{t('common.action.retry')}</Button>
        </output>
      ) : null}

      <div className={styles['withPanel']}>
        <ConstellationTree
          rows={rows}
          expanded={expanded}
          work={work}
          rollup={rollup.data}
          sumsScope={sumsScope}
          owners={owners.data}
          selectedElementId={selectedId}
          primaryRowKey={primaryRowKey}
          onToggle={toggle}
          onShowAll={showAll}
          onSelect={select}
          onEnter={enter}
          onOpenCommitments={openCommitments}
          onClearSelection={clearSelection}
        />

        {/*
          Slot panelu stoi ZAWSZE, także bez zaznaczenia (makieta B0 §3a) — inaczej pierwsze
          kliknięcie zwężałoby drzewko o 29 rem i wiersz uciekałby spod kursora.
        */}
        <ElementInspector
          elementId={selectedId}
          title={selectedTitle}
          type={selectedType}
          inTree={selectedRows.length > 0}
          commitmentsFocus={
            wantsCommitments && selectedId !== null ? `${selectedId}:${commitmentsNonce}` : null
          }
          onClose={clearSelection}
        />
      </div>
    </div>
  );
}

/** Sufit po stronie serwera i tak przytnie, ale zły adres nie ma powodu tam jechać. */
function readCap(raw: string | null): number | null {
  if (raw === 'all') {
    return null;
  }

  const parsed = Number(raw);

  return parsed === 5 || parsed === 10 ? parsed : 7;
}

/**
 * Zaznaczony element z adresu — albo `null`, gdy nic nie jest zaznaczone.
 *
 * Adres bywa wpisywany ręcznie i wklejany (spec 4A), więc `el=abc` albo `el=-1` to
 * normalny przypadek, a nie awaria: panel po prostu zostaje pusty.
 */
function readSelectedElement(params: URLSearchParams): number | null {
  const id = Number(params.get('el'));

  return Number.isInteger(id) && id > 0 ? id : null;
}

/** Soczewka: `rel=has` znaczy „pokaż tylko `has`", czyli ukryj pozostałe. */
function readLenses(raw: string | null): ReadonlySet<RelationDirection> {
  if (!raw || raw === 'all') {
    return new Set();
  }

  const selected = LENSES.find((lens) => lens === raw);

  return selected ? new Set(LENSES.filter((lens) => lens !== selected)) : new Set();
}
